import { forwardRef, useState } from 'react';
import styled from 'styled-components';
import { getSpacingToken } from '@optimus-web/common';
import { IconName } from '@optimus-web/icons';
import { FieldProps } from '../Field';
import { InputProps } from '../Input';
import { Stack } from '../Stack';
import { TextInput } from '../TextInput';
import { PasswordRequirementList } from './PasswordRequirementList';
import { PasswordStatusBar } from './PasswordStatusBar';
import { PasswordRequirementData } from './types';

export type PasswordInputProps = InputProps &
    FieldProps & {
        passwordStrength?: {
            variant?: PasswordInputVariant;
            requirements: PasswordRequirementData[];
        };
        hasPasswordVisibilityToggle?: boolean;
    };

export type PasswordInputVariant = 'status-bar' | 'requirements';

export const PasswordInput = forwardRef(
    (
        { helper, hasPasswordVisibilityToggle, passwordStrength, value, error, ...other }: PasswordInputProps,
        ref: React.Ref<HTMLElement>
    ) => {
        const [isPasswordVisible, setIsPasswordVisible] = useState(false);

        const handleTogglePasswordVisibility = () => {
            setIsPasswordVisible(!isPasswordVisible);
        };

        const inputFieldProps = hasPasswordVisibilityToggle
            ? {
                  type: isPasswordVisible ? 'text' : 'password',
                  interactiveIcon: isPasswordVisible ? IconName.ViewOff : IconName.Show,
                  interactiveIconHandler: handleTogglePasswordVisibility,
              }
            : {
                  type: 'password',
              };

        return (
            <TextInput
                value={value}
                // We want to hide the default error if we show the password requirements
                error={passwordStrength?.variant !== 'requirements' && error}
                {...other}
                helper={
                    <PasswordInputHelper
                        passwordStrength={passwordStrength}
                        value={value}
                        helper={helper}
                        error={error}
                    />
                }
                {...inputFieldProps}
                ref={ref}
            />
        );
    }
);

function PasswordInputHelper({
    passwordStrength,
    value,
    helper,
    error,
}: Pick<PasswordInputProps, 'passwordStrength' | 'value' | 'helper' | 'error'>) {
    if (!passwordStrength) {
        return <>{helper}</>;
    }

    // Parse value to string to avoid errors when checking password requirements
    const parsedValue = (value || '').toString();
    const { variant = 'status-bar', requirements } = passwordStrength;

    if (variant === 'status-bar') {
        return (
            <Stack spacing="100" vertical>
                <PasswordStatusBar value={parsedValue} requirements={requirements} />
                {helper}
            </Stack>
        );
    }

    if (variant === 'requirements') {
        return (
            <Stack spacing="50" vertical>
                {helper}
                <PasswordRequirementListContainer>
                    <PasswordRequirementList value={parsedValue} requirements={requirements} error={error} />
                </PasswordRequirementListContainer>
            </Stack>
        );
    }

    return null;
}

const PasswordRequirementListContainer = styled.div`
    padding-top: ${getSpacingToken('50')}px;
`;
